import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Accordion from '../../../components/Accordion/Accordion';
import Select from '../../../components/Select/Select';
import Input from '../../../components/Input/Input';
import Loader from '../../../components/Loader/Loader';
import './ViewUsers.css';
import {
    selectAllUsers,
    selectUserTypes,
    getUserTypes,
    putUserStatus,
    selectUserSearch,
    selectUserTypesLoadingStatus,
    setUserTypeLoadingStatus,
    putUserType,
    setUserTypesArray
} from '../../../store/delegationOfAuthoritySlice';
import {
    deepCopy,
    createTimeStamp
} from '../../../store/quotesAndNotificationsSlice';
import {
    selectRole,
    selectUserPermissions
} from '../../../store/pageLayoutSlice';
import {
    apiCall
} from '../../../APICalls/APICalls';

function ViewUsers(props){

    const users = useSelector( selectAllUsers );
    const userTypes = useSelector( selectUserTypes );
    const { name, role, userType, userStatus } = useSelector( selectUserSearch );
    let initials, colorIndex, headContent;
    const colorsArray = [
        '#33adff',
        '#ff6600',
        '#007a3d',
        '#213943',
        '#c7e0f4'
    ];
    
    const filteredUsers = useMemo(() => {
        let filtered = users;
        if( name !== '' ){
            let _name = name.toLowerCase();
            filtered = filtered.filter( user => user.userName.toLowerCase().includes( _name ) );
        }
        if( userType !== '' ){
            let _userType = userType.toLowerCase();
            filtered = filtered.filter( user => user.userType.toLowerCase().includes( _userType ) );
        }
        if( userStatus !== '' ){
            let _userStatus = userStatus.toLowerCase();
            filtered = filtered.filter( user => user.userStatus.toLowerCase().includes( _userStatus ) );
        }
        if( role !== '' ){
            let _role = role.toLowerCase();
            filtered = filtered.filter( user => user.role.toLowerCase().includes( _role ) );
        }
        return filtered;
    }, [ users, name, role, userType, userStatus ]);

    const userList = filteredUsers.map((user, i) => {
        initials = user.userName.match( /\b(\w)/g ).join('').slice(0, 2).toUpperCase(); //get initials.
        colorIndex = Math.floor( Math.random() * ( colorsArray.length ) );
        headContent = <div className="user_list_item">
                        <div className="user_list_username">
                            {/* <span 
                                style={{
                                    display: 'inline-block',
                                    width: '27px',
                                    padding: '0.2rem',
                                    backgroundColor: colorsArray[ colorIndex ],
                                    fontWeight: 600,
                                    color: 'white',
                                    borderRadius: '50%',
                                    textAlign: 'center',
                                    marginRight: '5px'
                                }}
                            >
                                { initials }
                            </span> */}
                            <svg height="28" width="28">
                                <circle cx="14" cy="14" r="14" fill={ colorsArray[ colorIndex ] } />
                                <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fontWeight="600" fontSize="14px" fill="white">{initials}</text>
                            </svg>
                            <span>{ user.userName }</span>
                        </div>
                        <div className="user_list_usertype">
                            <span>{ user.userType }</span>
                        </div>
                        <div className="user_list_userstatus">
                            <span>{ user.userStatus }</span>
                        </div>
                        <div className="user_list_role">
                            <span>{ user.role }</span>
                        </div>
                    </div>;
        return (
            <Accordion
                contentDescription="General Information"
                headContent={ headContent }
                isTopAccordion={ false }
                key={ user.userId }
            >
                <UserProfile user={user} userTypes={userTypes}/>
            </Accordion>
        );
    });
    return (
        <>
            {
                userList.length === 0 ?
                <div>
                    <p className="empty_message">There are no users that match the search values.</p>
                </div>
                :
                <>
                <ul className="user_list">
                    <li className="user_list_headers">
                        <div className="user_list_username">
                            <span>Name</span>
                        </div>
                        <div className="user_list_usertype">
                            <span>User Type</span>
                        </div>
                        <div className="user_list_userstatus">
                            <span>User Status</span>
                        </div>
                        <div className="user_list_role">
                            <span>User Role</span>
                        </div>
                    </li>
                </ul>
                <div className="user_list">
                    {userList}
                </div>
                </>
            }
        </>
    );
}

function UserProfile(props){

    const [ editUserAcc, setEditUser ] = useState(false);
    const [ user, setUser ] = useState(props.user);
    const dispatch = useDispatch();
    const adminUserRole = useSelector( selectRole );

    // The recovery path that does not depend on mail being deliverable. The
    // server returns the one-time password exactly once and stores only its
    // hash, so it is held in component state and shown until dismissed -
    // reloading the page loses it for good, and the admin has to reset again.
    const permissions = useSelector( selectUserPermissions );
    const canResetPassword = permissions.includes('Edit User');
    const [ resetBusy, setResetBusy ] = useState(false);
    const [ resetConfirming, setResetConfirming ] = useState(false);
    const [ issuedPassword, setIssuedPassword ] = useState('');
    const [ resetError, setResetError ] = useState('');
    const loading = useSelector( selectUserTypesLoadingStatus ).userTypesLoadingStatus;
    const userPropsUserType = props.user.userType;
    const userPropsUserRole = props.user.role;
    const userPropsUserStatus = props.user.userStatus;

    useEffect(() => {
        const userInfo = {...props.user};
        setUser(userInfo);
    }, [ userPropsUserType, userPropsUserRole, userPropsUserStatus ]);

    const {
        userName,
        userType,
        userStatus,
        userId,
        role
    } = { ...user };

    const userTypesAndRolls = {};
    props.userTypes.forEach(userTypeObj =>{
        userTypesAndRolls[ userTypeObj.userType ] = userTypeObj.roles;
    });

    function userAttributeChange(e, field, changeType){
        const user_temp = {...user};
        let value = changeType === 'change' ? e.target.value : e.target.textContent;
        user_temp[field] = value;
        if ( field === 'userType' ){
            user_temp['role'] = userTypesAndRolls[value][0];
        }
        setUser(user_temp);
    }

    function cancelUserEdit(){
        setEditUser(false);
        setUser(props.user);
    }

    function saveChanges(){
        let newUserTypeObjsArr = [];
        let userObj = deepCopy(user); //new copy of user object to be added/removed from activeUsers array
        delete userObj["role"]; //remove role and userType as they are not needed for backend storage
        delete userObj["userType"];
        if ( user.userType !== props.user.userType  ){ //send new versions of users old userType and user's new User Type objects
            dispatch( setUserTypeLoadingStatus( 'loading' ) );
            props.userTypes.forEach(userTypeObj => {
                if ( userTypeObj.userType === user.userType || userTypeObj.userType === props.user.userType ){

                    const newUserTypeObj = deepCopy(userTypeObj);
                    let oldUsersArr, newUsersArr, userRolesArr, userRoleObj, newUsersObj;

                    if ( userTypeObj.userType === user.userType ){ // add user to new userType

                        userRolesArr = newUserTypeObj.userRoles;
                        userRoleObj = userRolesArr.find(userRole => userRole.role === user.role);
                        newUsersObj = deepCopy(userRoleObj.users[0]);
                        userRoleObj.users[0] = newUsersObj;
                        userRoleObj.users[0].activeUsers.push( userObj ); //add user
                        userRoleObj.users[0].timestamp = createTimeStamp();

                    } else if ( userTypeObj.userType === props.user.userType ){// remove user from old userType

                        userRolesArr = newUserTypeObj.userRoles;
                        userRoleObj = userRolesArr.find(userRole => userRole.role === props.user.role);
                        oldUsersArr = userRoleObj.users;
                        newUsersArr = oldUsersArr;
                        newUsersArr[0] = deepCopy(oldUsersArr[0]);
                        newUsersArr[0].activeUsers = newUsersArr[0].activeUsers.filter( userObject => userObject.userId !== userId ); //remove user
                        newUsersArr[0].timestamp = createTimeStamp();

                    }
                    
                    newUserTypeObjsArr.push( newUserTypeObj );
                }
            });
            console.log("NEW USER TYPES ARR",newUserTypeObjsArr);
            newUserTypeObjsArr.forEach(type => console.log(JSON.stringify(type)));
            dispatch( setUserTypeLoadingStatus( '' ) );
            apiCall('DoA/putUserType', newUserTypeObjsArr[0]).then(res => {
                apiCall('DoA/putUserType', newUserTypeObjsArr[1]).then(response => {
                    setEditUser(false);
                    const newUserTypesArray = deepCopy( props.userTypes );
                    newUserTypeObjsArr.forEach( userTypeObj =>{
                        const newUserTypesIndex = newUserTypesArray.findIndex( newType => newType.userType === userTypeObj.userType );
                        if( newUserTypesIndex !== -1 ){
                            newUserTypesArray[ newUserTypesIndex ] = userTypeObj;
                        }
                    });
                    dispatch( setUserTypesArray(newUserTypesArray) );
                    dispatch( setUserTypeLoadingStatus( '' ) );
                }).catch(err =>{
                    console.log(err);
                    dispatch( setUserTypeLoadingStatus( 'rejected' ) );
                });
            }).catch(e =>{
                console.log(e);
                dispatch( setUserTypeLoadingStatus( 'rejected' ) );
            });

            // const calls = newUserTypeObjsArr.map( userType => apiCall('DoA/putUserType', userType ) );
            // Promise.all( calls ).then( responses => {
            //     console.log(responses);
            //     // dispatch( getUserTypes({app: 'hdmi'}) ).unwrap().then(res =>{
            //     //     setEditUser(false);
            //     // }).catch(error => {
            //     //     console.log(error);
            //     // });
                // setEditUser(false);
                // const newUserTypesArray = deepCopy( props.userTypes );
                // newUserTypeObjsArr.forEach( userTypeObj =>{
                //     const newUserTypesIndex = newUserTypesArray.findIndex( newType => newType.userType === userTypeObj.userType );
                //     if( newUserTypesIndex !== -1 ){
                //         newUserTypesArray[ newUserTypesIndex ] = userTypeObj;
                //     }
                // });
                // dispatch( setUserTypesArray(newUserTypesArray) );
                // dispatch( setUserTypeLoadingStatus( '' ) );
            // }).catch( err =>{
            //     console.log(err);
            //     dispatch( setUserTypeLoadingStatus( 'rejected' ) );
            // });

        } else if ( user.role !== props.user.role ){ //send new version of users userType object
            dispatch( setUserTypeLoadingStatus( 'loading' ) );
            const userTypeObj = deepCopy(props.userTypes.find(obj => obj.userType === user.userType));

            userTypeObj.userRoles.forEach(roleObj => {
                if ( roleObj.role === user.role || roleObj.role === props.user.role ){

                    let newUsersArr = roleObj.users;
                    //newUsersArr.unshift( deepCopy(newUsersArr[0]) );
                    if ( roleObj.role === user.role ){

                        newUsersArr[0].activeUsers.push( userObj ); //add user
                    } else if ( roleObj.role === props.user.role ){

                        newUsersArr[0].activeUsers = newUsersArr[0].activeUsers.filter( userObject => userObject.userId !== userId ); //remove user
                    }
                    newUsersArr[0].timestamp = createTimeStamp();

                }
            });

            console.log(JSON.stringify(userTypeObj));
            dispatch( putUserType( userTypeObj ) ).unwrap().then(res => {
                console.log(res);
                // dispatch( getUserTypes({app: 'hdmi'}) ).unwrap().then(res =>{
                //     setEditUser(false);
                // }).catch(error => {
                //     console.log(error);
                // });
                setEditUser(false);
                const newUserTypesArray = deepCopy( props.userTypes );
                const newUserTypesIndex = newUserTypesArray.findIndex( newType => newType.userType === userTypeObj.userType );
                console.log(newUserTypesIndex);
                if( newUserTypesIndex !== -1 ){
                    newUserTypesArray[ newUserTypesIndex ] = userTypeObj;
                    dispatch( setUserTypesArray(newUserTypesArray) );
                    dispatch( setUserTypeLoadingStatus( '' ) );
                }
            }).catch(err => {
                console.log(err);
            });

        } else if ( user.userStatus !== props.user.userStatus ){ //send user's info for user status to be updated
           // dispatch( setUserTypeLoadingStatus( 'loading' ) );

            const userTypeObj = deepCopy(props.userTypes.find(obj => obj.userType === user.userType));

            //console.log(userTypeObj);

            const userRoleIndex = userTypeObj.userRoles.findIndex(roleObj => roleObj.role === user.role )
            //console.log(userRoleIndex);
            if( userRoleIndex !== -1 ){
                const userObjIndex = userTypeObj.userRoles[ userRoleIndex ].users[0].activeUsers.findIndex( userObj => userObj.userId === user.userId );
                //console.log(userObjIndex);
                if( userObjIndex !== -1 ){
                    userTypeObj.userRoles[ userRoleIndex ].users[0].activeUsers[ userObjIndex ].userStatus = userStatus;
                    console.log(userTypeObj);
                    //console.log( userTypeObj.userRoles[ userRoleIndex ].users[0].activeUsers[ userObjIndex ] );
                    dispatch( putUserType( userTypeObj ) ).unwrap().then(res => {
                        setEditUser(false);
                        const newUserTypesArray = deepCopy( props.userTypes );
                        const newUserTypesIndex = newUserTypesArray.findIndex( newType => newType.userType === userTypeObj.userType );
                        if( newUserTypesIndex !== -1 ){
                            newUserTypesArray[ newUserTypesIndex ] = userTypeObj;
                            dispatch( setUserTypesArray(newUserTypesArray) );
                            dispatch( setUserTypeLoadingStatus('') );
                        }
                        //dispatch( setUserTypeLoadingStatus( 'loading' ) );
                        // setTimeout(() => {
                        //     dispatch( getUserTypes({app: 'hdmi'}) ).unwrap().then(res =>{
                        //         setEditUser(false);
                        //     }).catch(error => {
                        //         console.log(error);
                        //     });
                        // }, 3000);
                    }).catch(err => {
                        console.log(err);//61e148d4792e1a002fa68964
                    });
                }
            }            
        }
    }
    
    function handleResetPassword(){
        if ( resetBusy ) return;

        setResetBusy(true);
        setResetError('');

        apiCall('auth/adminResetPassword', { userId: userId }).then( res => {
            if ( res.data && res.data.temporaryPassword ) {
                setIssuedPassword( res.data.temporaryPassword );
                setResetConfirming(false);
            } else {
                setResetError('The reset did not return a password. Try again.');
            }
            setResetBusy(false);
        }).catch( error => {
            const status = error.response ? error.response.status : null;
            if ( status === 403 ) {
                setResetError('You do not have permission to reset passwords.');
            } else if ( status === 404 ) {
                setResetError('This user has no credential to reset. They may never have been provisioned.');
            } else {
                setResetError('Could not reset the password. Try again.');
            }
            setResetBusy(false);
        });
    }

    return (
        <div className="user_profile_wrapper">
            <div className="user_profile_header">
                <div>
                    <h3 className="user_profile_title">
                        { userName }
                    </h3>
                </div>
                {
                    !editUserAcc && canResetPassword &&
                    <button
                        className="user_profile_button reset_user_password"
                        onClick={ () => { setResetConfirming(true); setResetError(''); } }
                    >
                        Reset Password
                    </button>
                }
                {
                    editUserAcc && adminUserRole === 'Super Admin' &&
                    <> 
                        <button className="user_profile_button cancel_user_profile" onClick={ cancelUserEdit } >Cancel</button>
                        <button className="user_profile_button save_user_profile" onClick={ saveChanges } >Save Changes</button>
                    </>
                }
                {
                    !editUserAcc && adminUserRole === 'Super Admin' &&
                    <button className="user_profile_button edit_user_profile" onClick={ () => setEditUser(true) }>Edit</button> 
                }
            </div>
            {
                resetError &&
                <p className="reset_password_error">{ resetError }</p>
            }
            {
                resetConfirming &&
                <div className="reset_password_panel">
                    <p className="reset_password_text">
                        Reset the password for <strong>{ userName }</strong>? This signs
                        them out everywhere and issues a one-time password they must
                        change at their next sign-in.
                    </p>
                    <div className="reset_password_actions">
                        <button
                            className="user_profile_button save_user_profile"
                            onClick={ handleResetPassword }
                            disabled={ resetBusy }
                        >
                            { resetBusy ? 'Resetting...' : 'Confirm Reset' }
                        </button>
                        <button
                            className="user_profile_button cancel_user_profile"
                            onClick={ () => setResetConfirming(false) }
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            }
            {
                issuedPassword &&
                <div className="reset_password_panel reset_password_issued">
                    <p className="reset_password_text">
                        One-time password for <strong>{ userName }</strong>. This is the
                        only time it is shown - give it to them directly, not by email,
                        and close this when you are done.
                    </p>
                    <code className="reset_password_value">{ issuedPassword }</code>
                    <div className="reset_password_actions">
                        <button
                            className="user_profile_button cancel_user_profile"
                            onClick={ () => setIssuedPassword('') }
                        >
                            Done
                        </button>
                    </div>
                </div>
            }
            <div className="user_profile_type_and_status_container">
                <div className="user_profile_type_container user_profile_info_container">
                    {
                        editUserAcc ? 
                        <Select 
                            label="User Type"
                            value={ userType }
                            handleChange={ (e) => userAttributeChange(e, 'userType', 'change') }
                            placeHolder="Select a type"
                            options={ Object.keys(userTypesAndRolls) }
                        /> :
                        <Input 
                            type="display"
                            label="User Type"
                            value={ userType }
                        />
                    }
                </div>
                <div className="user_role_type_container user_profile_info_container">
                    {
                        editUserAcc ? 
                        <Select 
                            label="User Role"
                            value={ role }
                            handleChange={ (e) => userAttributeChange(e, 'role', 'change') }
                            placeHolder="Select a role"
                            options={ userTypesAndRolls[ userType ] }
                        /> :
                        <Input 
                            type="display"
                            label="User Role"
                            value={ role }
                        />
                    }
                </div>
                <div className="user_status_type_container user_profile_info_container">
                    {
                        editUserAcc ? 
                        <Select 
                            label="User Status"
                            value={ userStatus }
                            handleChange={ (e) => userAttributeChange(e, 'userStatus', 'change') }
                            placeHolder="Select a status"
                            options={['Available', 'Away', 'Deactivated']}
                        /> :
                        <Input 
                            type="display"
                            label="User Status"
                            value={ userStatus }
                        />
                    }
                </div>
            </div>
        </div>
    );
}

export default ViewUsers;